import { useEffect } from 'react';
import { create } from 'zustand';

import {
  snapshotVersionOf,
  withSnapshotVersion,
} from '@sb/database/snapshotParams';
import { makeLogNamespace } from '@sb/log/log';
import type { Space } from '@sb/routine-runner';
import { createGlobalSpace } from '@sbrc/services/feathers-client/global-space/createGlobalSpace';
import { deleteGlobalSpace } from '@sbrc/services/feathers-client/global-space/deleteGlobalSpace';
import { onGetAllGlobalSpace } from '@sbrc/services/feathers-client/global-space/onGetAllGlobalSpace';
import { updateGlobalSpace } from '@sbrc/services/feathers-client/global-space/updateGlobalSpace';
import { getGlobalSpaceService } from '@sbrc/services/feathers-client/global-space/utils/getGlobalSpaceService';

const log = makeLogNamespace('useGlobalSpaceStore');

interface GlobalSpaceStore {
  items: Space.Item[];
  itemIDToGlobalKey: { [key: string]: string };
  /** Resolves to the stored item, carrying the version the server assigned. */
  addItem: (item: Space.Item) => Promise<Space.Item>;
  /**
   * Write `item` on top of the row as this client last saw it. Editors keep
   * the copy they opened across saves, so the version is taken from the
   * store's row (the latest echo), not from the copy; the saved row then
   * replaces the store's row so a save that follows before the echo is
   * guarded too. Resolves to the stored item.
   */
  updateItem: (item: Space.Item) => Promise<Space.Item>;
  removeItem: (itemID: string) => Promise<void>;
}

export const useGlobalSpaceStore = create<GlobalSpaceStore>((set, get) => {
  /** Put a row the server just returned into the store ahead of its echo. */
  const remember = (globalSpaceID: string, spaceItem: Space.Item) => {
    set((state) => ({
      items: state.items.some((i) => i.id === spaceItem.id)
        ? state.items.map((i) => (i.id === spaceItem.id ? spaceItem : i))
        : [...state.items, spaceItem],
      itemIDToGlobalKey: {
        ...state.itemIDToGlobalKey,
        [spaceItem.id]: globalSpaceID,
      },
    }));
  };

  const addItem = async (item: Space.Item) => {
    try {
      const created = await createGlobalSpace(item);

      remember(created.globalSpaceID, created.spaceItem);

      return created.spaceItem;
    } catch (e) {
      log.error('addItem.error', 'Global space item did not add', { e });
      throw e;
    }
  };

  const updateItem = async (item: Space.Item) => {
    try {
      const globalItemID = get().itemIDToGlobalKey[item.id];
      const stored = get().items.find((i) => i.id === item.id);

      let version =
        (stored && snapshotVersionOf(stored)) ?? snapshotVersionOf(item);

      // Neither the store nor the copy knows the version (created elsewhere
      // and not echoed yet): read the row once rather than refuse the save.
      if (version === undefined) {
        version = (await getGlobalSpaceService().get(globalItemID)).updated;
      }

      const saved = await updateGlobalSpace(
        globalItemID,
        withSnapshotVersion({ ...item }, version),
      );

      remember(globalItemID, saved);

      return saved;
    } catch (e) {
      log.error('updateItem.error', 'Global space item did not update', { e });
      throw e;
    }
  };

  const removeItem = async (itemID: string) => {
    try {
      const globalItemID = get().itemIDToGlobalKey[itemID];
      await deleteGlobalSpace(globalItemID);
    } catch (e) {
      log.error('removeItem.error', 'Global space item did not remove', { e });
      throw e;
    }
  };

  return {
    items: [],
    itemIDToGlobalKey: {},
    addItem,
    updateItem,
    removeItem,
  };
});

export function useInitializeGlobalSpace() {
  useEffect(() => {
    const unsubscribeGlobalSpace = onGetAllGlobalSpace(
      (globalSpaceItemsWithID) => {
        const itemIDToGlobalKey: { [key: string]: string } = {};
        const globalSpaceItems = [];

        for (const itemWithID of globalSpaceItemsWithID) {
          itemIDToGlobalKey[itemWithID.spaceItem.id] = itemWithID.globalSpaceID;
          globalSpaceItems.push(itemWithID.spaceItem);
        }

        useGlobalSpaceStore.setState({
          items: globalSpaceItems,
          itemIDToGlobalKey,
        });
      },
    );

    return unsubscribeGlobalSpace;
  }, []);
}
